// ═════════════════════════════════════════════════════════════════════════════
//  Convention-based permissions — the frontend half.
//  Every module has exactly four permissions: view / create / update / delete. A button never asks for a permission of its
//  own: it says which of the four it performs, and everything else is worked out here.
//    view    the module (page, sidebar entry, tab) is shown and its data is read — without it the module is not shown at all
//    create  makes something new (add, new, import, duplicate, upload …)
//    update  changes something (edit, save, status change, approve, sync …)
//    delete  removes something
// ═════════════════════════════════════════════════════════════════════════════

export const ACTIONS = ["view", "create", "update", "delete"];

// Any action word → the CRUD action it really is. A new kind of button ("export", "approve", "clone"…) needs no new permission:
// it lands on its closest CRUD action by name (same table as the backend: utils/modulePermissions.js).
const ALIASES = {
  create: ["create", "add", "new", "import", "duplicate", "clone", "copy", "upload", "card", "cart", "invite", "connect", "generate", "request", "publish", "post"],
  update: ["update", "edit", "save", "change", "status", "toggle", "approve", "reject", "assign", "transfer", "share", "sync", "refresh", "reorder", "move", "enable", "disable", "set", "reset", "run"],
  delete: ["delete", "remove", "clear", "disconnect", "cancel", "archive", "revoke", "purge"],
};
export const crudActionOf = (name) => {
  const words = String(name || "").toLowerCase().split(/[^a-z]+/).filter(Boolean);
  if (words.includes("view") || words.includes("read") || words.includes("list")) return "view";
  for (const act of ["delete", "create", "update"]) if (words.some((w) => ALIASES[act].includes(w))) return act;
  return "view"; // anything that only looks at data
};

// The logged-in user's module whose path ends with "/<slug>" (the last part — or last parts — of the module's path).
export const findModule = (modules, slug) => (modules || []).find((m) => String(m.route_path || "").toLowerCase().replace(/\/+$/, "").endsWith(`/${String(slug).toLowerCase()}`));

// Does the user hold <action> on that module? `modules` are the permission-filtered modules the server gave at login, each with
// the codes the role holds ("admin.….orders.update"); the END of a code is its action.
export const moduleCan = (modules, slug, action) => {
  const m = findModule(modules, slug);
  if (!m) return false;
  const want = crudActionOf(action);
  return (m.permissions || []).some((c) => String(c).toLowerCase().split(".").pop() === want);
};

// The same check for the module the user is on RIGHT NOW (the one whose path is, or is the start of, the current URL) — for pages that two
// sides share (Account, Customers, the dashboard): no module name to write, the page asks about "this" module.
export const moduleCanAtPath = (modules, pathname, action) => {
  const here = String(pathname || "").replace(/\/+$/, "");
  const m = (modules || [])
    .filter((x) => x.route_path && (here === x.route_path.replace(/\/+$/, "") || here.startsWith(`${x.route_path.replace(/\/+$/, "")}/`)))
    .sort((a, b) => b.route_path.length - a.route_path.length)[0];
  if (!m) return false;
  const want = crudActionOf(action);
  return (m.permissions || []).some((c) => String(c).toLowerCase().split(".").pop() === want);
};

// Does this list of permission codes hold <type>?  type is any action word ("view", "edit", "export", "import" …) — it is turned into
// its CRUD action, and the END of a code is compared exactly (no "contains" matching: "budget" is not a "get").
export const hasAction = (codes, type) => {
  const want = crudActionOf(type);
  return (codes || []).some((code) => String(code).toLowerCase().split(".").pop() === want);
};
